/** @format */

import { cloneElement, forwardRef, useCallback, useEffect, useRef, useState, isValidElement, type ReactElement, type ReactNode, type Ref, type MutableRefObject, type MouseEvent as ReactMouseEvent } from 'react';
import { CrossIcon } from 'src/components/icons';

type Placement = 'bottom' | 'bottomRight' | 'bottomLeft' | 'top' | 'topRight' | 'topLeft';

export type CustomDropDownProps = {
  trigger: ReactElement;
  children?: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  placement?: Placement;
  className?: string;
  overlayClassName?: string;
  triggerOpensOnClick?: boolean;
  closeOnOutsideClick?: boolean;
  keepMounted?: boolean;
  closeOnPanelClick?: boolean;
  value?: unknown;
  defaultValue?: unknown;
  onReset?: (defaultValue: unknown) => void;
  resetAriaLabel?: string;
  resetIcon?: ReactNode;
  closeOnReset?: boolean;
};

const isSameValue = (a: unknown, b: unknown) => {
  if (a === b) return true;
  if (a == null && b == null) return true;
  return false;
};

const placementClassMap: Record<Placement, string> = {
  bottom: 'custom-dropdown__panel--bottom',
  bottomRight: 'custom-dropdown__panel--bottom-right',
  bottomLeft: 'custom-dropdown__panel--bottom-left',
  top: 'custom-dropdown__panel--top',
  topRight: 'custom-dropdown__panel--top-right',
  topLeft: 'custom-dropdown__panel--top-left'
};

const mergeRefs =
  <T,>(...refs: Array<Ref<T> | undefined>) =>
  (node: T | null) => {
    refs.forEach(r => {
      if (typeof r === 'function') r(node);
      else if (r) (r as MutableRefObject<T | null>).current = node;
    });
  };

export const CustomDropDown = forwardRef<HTMLDivElement, CustomDropDownProps>(function CustomDropDown(
  {
    trigger,
    children,
    open: openProp,
    defaultOpen = false,
    onOpenChange,
    placement = 'bottom',
    className = '',
    overlayClassName = '',
    triggerOpensOnClick = true,
    closeOnOutsideClick = true,
    keepMounted = false,
    closeOnPanelClick = true,
    value,
    defaultValue,
    onReset,
    resetAriaLabel = 'Reset to default',
    resetIcon = <CrossIcon width={10} height={10} />,
    closeOnReset = true
  },
  ref
) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const setRootRef = mergeRefs(rootRef, ref);

  const isControlled = openProp !== undefined;
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const open = isControlled ? openProp : internalOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setInternalOpen(next);
      }
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange]
  );

  useEffect(() => {
    if (!closeOnOutsideClick) return;
    const onDocMouseDown = (e: globalThis.MouseEvent) => {
      if (!open && !keepMounted) return;
      if (rootRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open, closeOnOutsideClick, keepMounted, setOpen]);

  const triggerNode = isValidElement(trigger)
    ? cloneElement(trigger, {
        ...(trigger.props as Record<string, unknown>),
        'aria-haspopup': (trigger.props as Record<string, unknown>)['aria-haspopup'] ?? 'menu',
        'aria-expanded': open,
        onClick: (e: ReactMouseEvent) => {
          (trigger.props as { onClick?: (event: ReactMouseEvent) => void }).onClick?.(e);
          if (!triggerOpensOnClick || e.defaultPrevented) return;
          if (isControlled) {
            onOpenChange?.(!openProp);
          } else {
            setInternalOpen(prev => !prev);
          }
        }
      } as Record<string, unknown>)
    : trigger;

  const showReset = defaultValue !== undefined && value !== undefined && !isSameValue(value, defaultValue);

  const handleResetClick = (e: ReactMouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onReset?.(defaultValue);
    if (closeOnReset) {
      setOpen(false);
    }
  };

  const triggerContent = showReset ? (
    <div className="custom-dropdown__trigger-row">
      {triggerNode}
      <button
        type="button"
        className="custom-dropdown__reset"
        aria-label={resetAriaLabel}
        onMouseDown={e => e.stopPropagation()}
        onClick={handleResetClick}>
        {resetIcon}
      </button>
    </div>
  ) : (
    triggerNode
  );

  const placementClass = placementClassMap[placement] || placementClassMap.bottom;
  const renderPanel = keepMounted || open;
  const panelHidden = keepMounted && !open;

  const handlePanelClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (!closeOnPanelClick || panelHidden) return;
    const interactive = (e.target as Element).closest(
      'a[href], button, [role="menuitem"], .ant-menu-item, .ant-dropdown-menu-item'
    );
    if (interactive) {
      setOpen(false);
    }
  };

  return (
    <div
      ref={setRootRef}
      className={`custom-dropdown${open ? ' custom-dropdown--open' : ''}${
        showReset ? ' custom-dropdown--has-reset' : ''
      }${className ? ` ${className}` : ''}`.trim()}>
      {triggerContent}
      {renderPanel ? (
        <div
          className={`custom-dropdown__panel ${placementClass}${
            overlayClassName ? ` ${overlayClassName}` : ''
          }${panelHidden ? ' custom-dropdown__panel--hidden' : ''}`.trim()}
          role="presentation"
          onClick={handlePanelClick}>
          {children}
        </div>
      ) : null}
    </div>
  );
});
